import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  createApiKey, listApiKeys, revokeApiKey,
  createWebhook, listWebhooks, deleteWebhook,
  getApiUsage
} from '../lib/api';
import { toast } from 'sonner';
import {
  Key, Webhook, Copy, Trash2, Plus, Loader2, Eye, EyeOff,
  BarChart3, CheckCircle2, AlertCircle, Code
} from 'lucide-react';
import { motion } from 'framer-motion';

const Developer = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [showWebhookDialog, setShowWebhookDialog] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [showSecretKey, setShowSecretKey] = useState(false);
  
  // Form states
  const [keyName, setKeyName] = useState('');
  const [keyPermissions, setKeyPermissions] = useState(['read']);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [creating, setCreating] = useState(false);

  const availableEvents = [
    { id: 'transaction.created', label: 'Transaction Created' },
    { id: 'transaction.updated', label: 'Transaction Updated' },
    { id: 'card.created', label: 'Card Created' },
    { id: 'card.frozen', label: 'Card Frozen' },
    { id: 'deposit.completed', label: 'Deposit Completed' },
    { id: 'transfer.completed', label: 'Transfer Completed' },
    { id: 'alert.triggered', label: 'Alert Triggered' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [keysRes, webhooksRes, usageRes] = await Promise.all([
        listApiKeys(),
        listWebhooks(),
        getApiUsage()
      ]);
      setApiKeys(keysRes.data.api_keys || []);
      setWebhooks(webhooksRes.data.webhooks || []);
      setUsage(usageRes.data);
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!keyName.trim()) {
      toast.error('Please enter a key name');
      return;
    }
    setCreating(true);
    try {
      const { data } = await createApiKey({ name: keyName, permissions: keyPermissions });
      setNewKeyResult(data);
      toast.success('API key created');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await revokeApiKey(keyId);
      toast.success('API key revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke key');
    }
  };

  const handleCreateWebhook = async () => {
    if (!webhookUrl.trim() || webhookEvents.length === 0) {
      toast.error('Please enter URL and select at least one event');
      return;
    }
    setCreating(true);
    try {
      await createWebhook({ url: webhookUrl, events: webhookEvents });
      toast.success('Webhook created');
      setShowWebhookDialog(false);
      setWebhookUrl('');
      setWebhookEvents([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await deleteWebhook(webhookId);
      toast.success('Webhook deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleEvent = (eventId) => {
    setWebhookEvents(prev => 
      prev.includes(eventId) 
        ? prev.filter(e => e !== eventId)
        : [...prev, eventId]
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        className="space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="developer-container"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Developer</h1>
          <p className="text-muted-foreground">Manage API keys and webhooks</p>
        </div>

        {/* Usage Stats */}
        {usage && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">API Keys</p>
                <p className="text-2xl font-bold">{usage.api_keys_count}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Webhooks</p>
                <p className="text-2xl font-bold">{usage.webhooks_count}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Requests Today</p>
                <p className="text-2xl font-bold">{usage.requests_today}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Webhook Deliveries</p>
                <p className="text-2xl font-bold text-emerald-500">{usage.webhook_deliveries?.successful || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="api-keys" className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-12 rounded-xl">
            <TabsTrigger value="api-keys" className="rounded-lg" data-testid="api-keys-tab">
              <Key className="w-4 h-4 mr-2" />
              API Keys
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="rounded-lg" data-testid="webhooks-tab">
              <Webhook className="w-4 h-4 mr-2" />
              Webhooks
            </TabsTrigger>
          </TabsList>

          <TabsContent value="api-keys" className="space-y-4 mt-6">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                API keys allow external applications to access your ParachPay data
              </p>
              <Button onClick={() => setShowApiKeyDialog(true)} className="rounded-xl" data-testid="create-api-key-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Key
              </Button>
            </div>

            <div className="space-y-4">
              {apiKeys.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Key className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No API keys yet</p>
                  </CardContent>
                </Card>
              ) : (
                apiKeys.map((key) => (
                  <Card key={key.id} data-testid={`api-key-${key.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">{key.name}</p>
                            <Badge variant="outline" className="text-xs">
                              {key.permissions?.join(', ')}
                            </Badge>
                          </div>
                          <p className="text-sm font-mono text-muted-foreground">{key.api_key}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Created: {new Date(key.created_at).toLocaleDateString()}
                            {key.last_used && ` • Last used: ${new Date(key.last_used).toLocaleDateString()}`}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="icon" onClick={() => copyToClipboard(key.api_key)}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleRevokeKey(key.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="webhooks" className="space-y-4 mt-6">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Webhooks send real-time notifications to your server
              </p>
              <Button onClick={() => setShowWebhookDialog(true)} className="rounded-xl" data-testid="create-webhook-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Webhook
              </Button>
            </div>

            <div className="space-y-4">
              {webhooks.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Webhook className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No webhooks configured</p>
                  </CardContent>
                </Card>
              ) : (
                webhooks.map((webhook) => (
                  <Card key={webhook.id} data-testid={`webhook-${webhook.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={webhook.status === 'active' ? 'bg-emerald-500/20 text-emerald-600 border-0' : 'bg-red-500/20 text-red-600 border-0'}>
                              {webhook.status === 'active' ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                              {webhook.status}
                            </Badge>
                          </div>
                          <p className="text-sm font-mono truncate">{webhook.url}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {webhook.events?.map(event => (
                              <Badge key={event} variant="outline" className="text-xs">{event}</Badge>
                            ))}
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteWebhook(webhook.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* API Documentation Link */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <Code className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold">API Documentation</h3>
                <p className="text-sm text-muted-foreground">Learn how to integrate ParachPay into your applications</p>
              </div>
              <Button variant="outline" className="rounded-xl">View Docs</Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Create API Key Dialog */}
      <Dialog open={showApiKeyDialog} onOpenChange={(open) => {
        setShowApiKeyDialog(open);
        if (!open) {
          setNewKeyResult(null);
          setKeyName('');
          setShowSecretKey(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{newKeyResult ? 'API Key Created' : 'Create API Key'}</DialogTitle>
            <DialogDescription>
              {newKeyResult ? 'Save your secret key now. It won\'t be shown again.' : 'Create a new API key for external access'}
            </DialogDescription>
          </DialogHeader>
          
          {newKeyResult ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>API Key</Label>
                <div className="flex gap-2">
                  <Input value={newKeyResult.api_key} readOnly className="font-mono text-sm" />
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(newKeyResult.api_key)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Secret Key (save this now!)</Label>
                <div className="flex gap-2">
                  <Input 
                    type={showSecretKey ? 'text' : 'password'} 
                    value={newKeyResult.secret_key} 
                    readOnly 
                    className="font-mono text-sm" 
                  />
                  <Button variant="outline" size="icon" onClick={() => setShowSecretKey(!showSecretKey)}>
                    {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(newKeyResult.secret_key)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/10 text-amber-600 text-sm">
                This secret key will only be shown once. Make sure to save it securely.
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Key Name</Label>
                <Input
                  placeholder="My Application"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  className="rounded-xl"
                  data-testid="api-key-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Permissions</Label>
                <Select value={keyPermissions[0]} onValueChange={(v) => setKeyPermissions([v])}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read">Read Only</SelectItem>
                    <SelectItem value="read,write">Read & Write</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            {newKeyResult ? (
              <Button onClick={() => setShowApiKeyDialog(false)} className="rounded-xl">Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowApiKeyDialog(false)} className="rounded-xl">Cancel</Button>
                <Button onClick={handleCreateApiKey} disabled={creating} className="rounded-xl" data-testid="create-key-submit">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Key'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Webhook Dialog */}
      <Dialog open={showWebhookDialog} onOpenChange={setShowWebhookDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Webhook</DialogTitle>
            <DialogDescription>Configure a webhook endpoint to receive events</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Endpoint URL</Label>
              <Input
                placeholder="https://your-server.com/webhooks/parachpay"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                className="rounded-xl"
                data-testid="webhook-url-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Events</Label>
              <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto p-2 border rounded-xl">
                {availableEvents.map(event => (
                  <label key={event.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={webhookEvents.includes(event.id)}
                      onCheckedChange={() => toggleEvent(event.id)}
                    />
                    <span className="text-sm">{event.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWebhookDialog(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleCreateWebhook} disabled={creating} className="rounded-xl" data-testid="create-webhook-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Webhook'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Developer;
